// What the site keeps about a person, and why (B-417, PLAN-2026-10-18 §9).
//
// A STRANGER TYPING AN EMAIL EXPECTS ONE, and the answer is short because the
// site keeps little: ACCOUNTS.md's table of what is stored, in plain words,
// with what is not, how long the few logs live (the retention job's own
// figures, `workers/ingest/src/retention.ts`), and what a person can do about
// any of it. Every sentence here is a claim about the code; change one with
// the thing it describes.
//
// PUBLIC, AND OUTSIDE `Shell`, as About is: it is read before an account
// exists. Its own scroll, since the browser never scrolls (DESIGN §4), and a
// way back to wherever it was opened from.

import { Link, useNavigate } from 'react-router-dom';
import { BRAND } from '../branding';
import { Button, Heading, Stack, Text, Title } from '../kit/ui';
import '../styles/screens/account.css';

/** When these words last changed. */
const UPDATED = 'October 31, 2026';

function Part({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="fsp-privacy-part">
      <Stack gap={2}>
        <Heading>{title}</Heading>
        {children}
      </Stack>
    </section>
  );
}

export function PrivacyScreen() {
  const navigate = useNavigate();
  return (
    <div className="fsp-privacy-page">
      <div className="fsp-about fsp-privacy">
        <Stack gap={5}>
          <div>
            <Button
              size="xs"
              variant="quiet"
              icon="back"
              // Back where it was opened from; opened on its own, the front page.
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            >
              Back
            </Button>
          </div>
          <Stack gap={1}>
            <Title>Privacy</Title>
            <Text tone="secondary" block>
              {`What ${BRAND.name} keeps about you, and why. Short, because we keep little. Updated ${UPDATED}.`}
            </Text>
          </Stack>

          <Part title="Who it is for">
            <Text block>
              Accounts are for people 13 and over. We ask no birthday, so the sign-up says so where
              the account is made.
            </Text>
          </Part>

          <Part title="What we keep about you">
            <ul className="fsp-privacy-list">
              <li>
                <strong>Your name on the site</strong>, the one other managers see. It does not have
                to be your real name.
              </li>
              <li>
                <strong>Your email address</strong>, to sign you in, to send the account mail you
                ask for (a password reset, the link that confirms the address), and once you confirm
                it, the league email your notification settings choose, such as the week’s recap.
                Each of those has a link that stops it, apart from the few you can’t miss, which
                come by email only when none of your devices can be told.
              </li>
              <li>
                <strong>Your password</strong>, stored only as a one-way hash, never as you typed
                it.
              </li>
              <li>
                <strong>Your teams</strong>: their names, colors and badges, league by league.
              </li>
              <li>
                <strong>Your choices</strong>: the look, the detail level, which notifications you
                want, and similar settings.
              </li>
              <li>
                <strong>Your sign-ins</strong>: one for each device you sign in on, with a rough
                device name so you can tell them apart and sign one out.
              </li>
              <li>
                <strong>Push notifications</strong>, only if you turn them on for a device.
              </li>
            </ul>
          </Part>

          <Part title="What happens in your leagues">
            <Text block>
              Your lineups, moves and trades, and what you say in a league’s chat, are seen by every
              member of the league; a direct message only by the two of you. Your team’s moves are
              part of the league’s record and stay with it, so its history still makes sense after a
              season ends.
            </Text>
            <Text block>
              We also keep a short log of a few things you do that no other record shows — signing
              in, saving a lineup, changing your settings or your team, trying a feature, finishing
              a mock draft — so achievements can be earned for them. Each entry is kept 90 days;
              after that only a count remains.
            </Text>
          </Part>

          <Part title="When something breaks">
            <Text block>
              If a page fails in your browser, the site sends us the error, the page it happened on
              and, if you are signed in, your account id, so we can fix it. Those reports are
              deleted after 30 days.
            </Text>
          </Part>

          <Part title="What we do not keep">
            <Text block>
              No real name, phone number, birthday or address. No IP address on your account: to
              slow down password guessing we keep a scrambled form of it for a short time, and
              nothing else. No advertising, no tracking ids, no analytics profiles, and nothing is
              ever sold or shared for marketing.
            </Text>
          </Part>

          <Part title="Cookies and your device">
            <Text block>
              One cookie, to keep you signed in. Your device also remembers small conveniences for
              itself, such as the tab or filter you last used; that stays on the device and is never
              sent to us.
            </Text>
          </Part>

          <Part title="Email">
            <Text block>
              We send a link to confirm your address, password resets and the league mail you turn
              on, from noreply@fspleagues.com. Notification settings choose which.
            </Text>
          </Part>

          <Part title="Where it lives">
            <Text block>
              The site and its database run on Cloudflare, which stores the data above on our
              behalf.
            </Text>
            <Text block>
              Everything a page needs comes from this site, fonts included, so opening one tells no
              other company that you visited. A link you follow to another site, such as a player’s
              page on ESPN, opens that site, and what it keeps is its own business.
            </Text>
          </Part>

          <Part title="Where the numbers come from">
            <Text block>
              Players, schedules, stats and injury reports come from open data shared freely by the
              people who make it.{' '}
              <Link className="fsp-linklike" to="/credits">
                The credits
              </Link>{' '}
              name them, with thanks.
            </Text>
          </Part>

          <Part title="Your choices">
            <Text block>
              On your Account page you can change your name, email and password, sign other devices
              out, download your data as a file, and delete your account.
            </Text>
            <Text block>
              Deleting asks for your password and happens at once. Your account goes, and with it
              everything above that is yours, everything you said in your leagues (chat and direct
              messages, reactions and poll votes), and the error reports your browser sent.
            </Text>
            <Text block>
              Your teams stay in their leagues, their record shown as a former manager’s: a
              co-manager takes yours over, or a bot plays it through a season under way, and a
              league with nobody else in it closes. Two things keep what they were: a league vote
              already decided keeps its count, because the league decided by it, and a few of the
              league’s own messages, such as one about a rule change you proposed, keep the name
              they were written with.
            </Text>
          </Part>
        </Stack>
      </div>
    </div>
  );
}
